import React, { useMemo, useState } from "react";
import Modal from "../modals/modal/Modal";
import styles from "./TrialBanner.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useAppSelector } from "../../services/hooks";
import { behavioralFeatureUsageSelector } from "../../services/selectors/entitlements.selectors";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

// sessionStorage rather than redux/localStorage: the popup should greet the user once per
// session, not once ever (localStorage) or on every remount (component state).
const DISMISSED_KEY = "trial_banner_dismissed";

const wasDismissedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === "1";
  } catch (error) {
    return false;
  }
};

const markDismissedForSession = (): void => {
  try {
    sessionStorage.setItem(DISMISSED_KEY, "1");
  } catch (error) {
    // Storage can be unavailable (private mode, blocked cookies) — worst case the popup
    // reappears next reload, which is harmless.
  }
};

type TrialBannerProps = {
  endsAt: string | null;
};

function formatFeatureLabel(key: string): string {
  return key
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const TrialBanner: React.FC<TrialBannerProps> = ({ endsAt }) => {
  const [dismissed, setDismissed] = useState(wasDismissedThisSession);
  const [showReducedFeatures, setShowReducedFeatures] = useState(false);

  const behavioralUsage = useAppSelector(behavioralFeatureUsageSelector);
  const reducedFeatures = useMemo(
    () => behavioralUsage.filter((u) => u.trialReduced),
    [behavioralUsage]
  );
  const hasReducedFeatures = reducedFeatures.length > 0;

  const { expiringSoon, days, expired } = useMemo(() => {
    if (!endsAt) {
      return { expiringSoon: false, days: 0, expired: false };
    }
    const ms = new Date(endsAt).getTime() - Date.now();
    if (ms <= 0) {
      return { expiringSoon: false, days: 0, expired: true };
    }
    return {
      expiringSoon: ms <= SEVEN_DAYS_MS,
      days: Math.ceil(ms / DAY_MS),
      expired: false,
    };
  }, [endsAt]);

  if (dismissed || expired) {
    return null;
  }

  const handleClose = () => {
    markDismissedForSession();
    setDismissed(true);
  };

  const bannerParams = { count: days, days };
  const message = expiringSoon
    ? t("TRIAL_EXPIRING_SOON_BANNER", bannerParams)
    : t("TRIAL_ACTIVE_BANNER", bannerParams);

  return (
    <Modal onClose={handleClose}>
      <div role={expiringSoon ? "alert" : "status"}>
        <p
          className={`${styles.message} ${
            expiringSoon ? styles.messageWarning : styles.messageInfo
          }`}
        >
          {message}
        </p>
        {hasReducedFeatures && (
          <div className={styles.secondaryLine}>
            {t("TRIAL_FEATURES_LIMITED")}{" "}
            <button
              className={styles.seeWhatLink}
              onClick={() => setShowReducedFeatures((o) => !o)}
              aria-expanded={showReducedFeatures}
            >
              {t("TRIAL_SEE_WHATS_LIMITED")}
            </button>
            {showReducedFeatures && (
              <ul className={styles.featureList}>
                {reducedFeatures.map((u) => (
                  <li key={u.feature}>{formatFeatureLabel(u.feature)}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TrialBanner;
